import { Capacitor } from '@capacitor/core';
import type { ILigaEvent } from '@/model/liga-event.model';

const ENV_API_URL = process.env.VUE_APP_API_URL?.trim() || '';

/** Без двойного `/` перед `api` (иначе Spring firewall и antMatchers ломаются). */
function joinApiBase(base: string, path: string): string {
  const b = base.replace(/\/+$/, '');
  const p = path.replace(/^\/+/, '');
  return b ? `${b}/${p}` : `/${p}`;
}

/**
 * URL публичной витрины без axios: так запрос никогда не получит чужой Bearer из перехватчика/defaults.
 * Логика совпадает с axiosBaseURL в axios-interceptor.ts (dev + прокси → относительный путь).
 */
export function resolvePublicShowcaseUrl(): string {
  if (!ENV_API_URL) {
    return '/api/public/showcase';
  }
  if (Capacitor.isNativePlatform()) {
    return joinApiBase(ENV_API_URL, 'api/public/showcase');
  }
  if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
    try {
      const normalized = ENV_API_URL.endsWith('/') ? ENV_API_URL : `${ENV_API_URL}/`;
      if (new URL(normalized).origin !== window.location.origin) {
        return '/api/public/showcase';
      }
    } catch {
      /* use absolute below */
    }
  }
  return joinApiBase(ENV_API_URL, 'api/public/showcase');
}

export interface PublicShowcaseEducationVideo {
  id: number;
  displayName: string;
  sectionFolderKey: string;
}

export interface PublicShowcaseEducationSection {
  folderKey: string;
  name: string;
  sortOrder: number;
  videos: PublicShowcaseEducationVideo[];
}

export interface PublicShowcaseRelease {
  id: number;
  releaseName: string;
  programId: number;
  contentSource: string;
}

export interface PublicShowcaseProgram {
  id: number;
  programName: string;
  shortName: string;
  totalReleasesCount?: number;
  releases: PublicShowcaseRelease[];
}

export interface PublicShowcaseSubscription {
  id: number;
  title: string;
  description?: string;
  logoPath?: string;
  durationDays?: number;
  priceTiers: import('@/model/liga-event.model').IEventPriceTier[];
}

export interface PublicShowcaseResponse {
  subscriptions?: PublicShowcaseSubscription[];
  events: ILigaEvent[];
  programs: PublicShowcaseProgram[];
  educationSections: PublicShowcaseEducationSection[];
}

export async function fetchPublicShowcase(): Promise<PublicShowcaseResponse> {
  const url = resolvePublicShowcaseUrl();
  const res = await fetch(url, {
    method: 'GET',
    cache: 'no-store',
    credentials: 'omit',
    headers: {
      Accept: 'application/json',
    },
  });
  if (!res.ok) {
    const err = new Error(`Showcase request failed: ${res.status}`) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  return res.json() as Promise<PublicShowcaseResponse>;
}
